import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouterScrollBehavior,
} from "vue-router";

import { useApiClient } from "@/composables/useApiClient";
import { useUtils } from "@/composables/useUtils";

import { useFormStore } from "@/stores/form";
import { checkBikePossession, checkMobileAccess } from "./util";

import { useApiStore } from "@/stores/api";
import { logJourneyAccessOnce } from "@/composables/useJourneyAccessTracking";
import { appRoutes } from "./appRoutes";
import { usePostHog } from "@/composables/usePostHog.ts";
import { useNewRelic } from "@/composables/useNewRelic";
// import { devRoutes } from "./devRoutes";

const util = useUtils();
const { posthog } = usePostHog();
const { newrelic } = useNewRelic();
const routes = [] as typeof appRoutes;

// if (util.isDevelopment) {
//   devRoutes.forEach((route) => {
//     route.isDevRoute = true;
//   });

//   routes.push(...devRoutes);
// }

routes.push(...appRoutes);

const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) {
    return savedPosition;
  }
  return { top: 0, left: 0 };
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior,
});

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  util.log(`Navigating from ${String(from.name)} to ${String(to.name)}`);
  newrelic?.setCurrentRouteName(String(to.name));

  const apiClient = useApiClient();
  logJourneyAccessOnce(to, { sendEvent: (payload) => apiClient.logJourneyAccessEvent(payload) });

  const api = useApiStore();

  // 1. Mobile/Desktop check
  if (!util.isDesktopJourneyEnabled) {
    const isDevLocalStorage = localStorage.getItem("isDev");
    const mobileRedirect = checkMobileAccess(to, util);
    if (mobileRedirect && isDevLocalStorage !== "true") {
      return mobileRedirect;
    }
  }

  // 2. Bike possession check
  const form = useFormStore();
  const bikeRedirect = checkBikePossession(to, form);
  if (bikeRedirect) return bikeRedirect;

  // 3. Insurance/Checklist check
  // const insuranceRedirect = await checkInsuranceAndRedirect(to, util, api);
  // if (insuranceRedirect) return insuranceRedirect;

  // 4. Refresh the per-screen completion checklist so revisited screens render
  // the "already completed" state instead of their inputs. Decoupled from the
  // redirect logic above; never blocks navigation if the request fails.
  if (api.insuranceCode) {
    try {
      await api.getChecklist();
    } catch (error) {
      util.log(`Failed to refresh checklist: ${String(error)}`);
    }
  }

  util.log(`Navigation to ${String(to.name)} is allowed.`);
});

router.afterEach((to) => {
  posthog.capture("$pageview", {
    $current_url: window.location.origin + to.fullPath,
  });
});

export default router;
